import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { Badge } from '@/components/ui/badge';
import { ROLE_LABELS, type ProducerMember } from '@/types/team';
import { ShieldAlert } from 'lucide-react';

interface TransferOwnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: ProducerMember[];
  onConfirm: (newOwnerUserId: string) => Promise<void>;
}

export function TransferOwnerDialog({
  open,
  onOpenChange,
  members,
  onConfirm,
}: TransferOwnerDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  // Filter eligible members (all active members except self - caller is implicitly the current admin)
  const eligibleMembers = members.filter(
    (m) => m.status === 'active'
  );

  const selectedMember = members.find((m) => m.userId === selectedUserId);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep(1);
      setSelectedUserId(null);
      setConfirmText('');
    }
    onOpenChange(newOpen);
  };

  const handleSelectMember = (userId: string) => {
    setSelectedUserId(userId);
    setStep(2);
  };

  const handleConfirm = async () => {
    if (!selectedUserId || confirmText !== 'BYT ÄGARE') return;
    setIsTransferring(true);
    try {
      await onConfirm(selectedUserId);
      handleOpenChange(false);
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <AlertDialogTitle>
              {step === 1 ? 'Välj ny ägare' : 'Bekräfta ägarbyte'}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            {step === 1
              ? 'Välj den person som ska bli ny ägare av gården. Du blir Medvärd efter bytet.'
              : `Du överför ägarskapet till ${selectedMember?.name || selectedMember?.email}. Din roll ändras till Medvärd.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {step === 1 ? (
          <div className="space-y-2 py-2">
            {eligibleMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Det finns inga aktiva medlemmar att överföra ägarskapet till.
                Bjud in någon först.
              </p>
            ) : (
              eligibleMembers.map((member) => (
                <button
                  key={member.userId}
                  onClick={() => handleSelectMember(member.userId!)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left"
                >
                  <FarmlyAvatar
                    imageUrl={member.avatarUrl}
                    initials={(member.name || member.email).slice(0, 2).toUpperCase()}
                    alt={member.name || member.email}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {member.name || member.email}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                  </div>
                  <Badge variant="outline">{ROLE_LABELS[member.role]}</Badge>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-sm text-destructive font-medium">
                Detta kan inte ångras utan att den nya ägaren gör ett nytt ägarbyte.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmTransfer">
                Skriv <strong>BYT ÄGARE</strong> för att bekräfta
              </Label>
              <Input
                id="confirmTransfer"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="BYT ÄGARE"
                autoComplete="off"
              />
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isTransferring}>
            Avbryt
          </Button>
          {step === 2 && (
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={confirmText !== 'BYT ÄGARE' || isTransferring}
            >
              {isTransferring ? 'Överför...' : 'Överför ägarskap'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
